export interface KabbalahDesignTemplateSettings_N {
  // The template's design, authored in the TipTap editor. Each record's block
  // is a <div data-kb-layout="entry">; the renderer fills the Nth block from
  // the Nth record, swapping {fullName} / {duration} / … for its values.
  html: string;
  // The card the design is drawn on — the box sitting behind the names. A hex
  // colour, or 'transparent' so the design sits straight on whatever is behind
  // it.
  borderColor: string;
  backgroundColor: string;
  // Number of cards shown across one row of the Kabbalah layout.
  columns: 1 | 2 | 3;
  // Padding inside each rendered card, stored as "verticalpx horizontalpx".
  cardPadding: string;
  // Space between rendered cards, in pixels.
  spacing: number;
}

export interface KabbalahDesignTemplate_N {
  id: string;
  label: string;
  description: string;
  // How many entry blocks the layout ships with. The preview fills them from
  // real records and pads the rest, so the design always reads the way it will
  // on a full screen.
  entryCount: number;
  // Only the markup differs between templates — the card colours all start
  // from the shared defaults below.
  defaults: Pick<KabbalahDesignTemplateSettings_N, 'html'>;
}

// Every template renders onto the same canvas, so a card thumbnail is a true
// miniature of the live preview — the renderer only ever scales this box.
//
// `width` is fixed: it is what makes line breaks land identically in the editor
// and the preview. `height` is only the nominal shape a design is drawn
// against — neither surface is pinned to it. Both size to their content and
// share the floor below, so the same design is exactly as tall in the editor as
// it is in the preview.
export const KABBALAH_TEMPLATE_CANVAS = { width: 560, height: 380 };

// The smallest a canvas may get: enough that an emptied design still has
// somewhere to click, small enough that a real one never inherits blank space.
// The rendered card adds its current vertical padding around this content area.
export const KABBALAH_CANVAS_MIN_CONTENT_HEIGHT = 120;

/* --------------------------------- colours ---------------------------------
 * The card behind the design.
 */
export const KABBALAH_CARD_BORDER_COLOR_DEFAULT_N = '#E6E8EE';
export const KABBALAH_CARD_BACKGROUND_COLOR_DEFAULT_N = '#FFFFFF';
export const KABBALAH_CARD_PADDING_DEFAULT_VERTICAL_N = 12;
export const KABBALAH_CARD_PADDING_DEFAULT_HORIZONTAL_N = 14;
export const KABBALAH_CARD_PADDING_DEFAULT_N =
  `${KABBALAH_CARD_PADDING_DEFAULT_VERTICAL_N}px ${KABBALAH_CARD_PADDING_DEFAULT_HORIZONTAL_N}px`;
export const KABBALAH_CARD_PADDING_MIN_N = 0;
export const KABBALAH_CARD_PADDING_MAX_N = 200;
export const KABBALAH_CARD_SPACING_DEFAULT_N = 12;
export const KABBALAH_CARD_SPACING_MIN_N = 0;
export const KABBALAH_CARD_SPACING_MAX_N = 120;
// The editor caps a custom design at the original 560px card width, but the
// rendered card itself is allowed to hug its content and grow only by padding.
export const KABBALAH_TEMPLATE_CONTENT_WIDTH =
  KABBALAH_TEMPLATE_CANVAS.width - 28 * 2;
export const KABBALAH_CANVAS_MIN_HEIGHT =
  KABBALAH_CANVAS_MIN_CONTENT_HEIGHT + KABBALAH_CARD_PADDING_DEFAULT_VERTICAL_N * 2;

// The keyword the background field accepts alongside a hex value.
export const KABBALAH_TRANSPARENT_COLOR_N = 'transparent';

export const normalizeKabbalahCardColumns_N = (value: unknown): 1 | 2 | 3 => {
  const columns = Number(value);
  return columns === 2 || columns === 3 ? columns : 1;
};

export const getKabbalahCardPaddingParts_N = (
  value: unknown = KABBALAH_CARD_PADDING_DEFAULT_N
): { vertical: number; horizontal: number } => {
  const fallback = {
    vertical: KABBALAH_CARD_PADDING_DEFAULT_VERTICAL_N,
    horizontal: KABBALAH_CARD_PADDING_DEFAULT_HORIZONTAL_N,
  };
  const raw = String(value ?? '').trim().replace(/\s+/g, ' ');
  if (!raw) return fallback;

  const tokens = raw.split(' ');
  if (tokens.length > 4) return fallback;

  const normalizedTokens = tokens.map((token) => {
    const match = token.match(/^(\d+(?:\.\d+)?)(px)?$/i);
    if (!match) return null;
    const numericValue = Math.min(
      Math.max(Number(match[1]), KABBALAH_CARD_PADDING_MIN_N),
      KABBALAH_CARD_PADDING_MAX_N
    );
    return Number.isInteger(numericValue)
      ? numericValue
      : Number(numericValue.toFixed(2));
  });

  if (!normalizedTokens.every((token) => token !== null)) return fallback;

  return {
    vertical: normalizedTokens[0] as number,
    horizontal: (normalizedTokens[1] ?? normalizedTokens[0]) as number,
  };
};

export const normalizeKabbalahCardPadding_N = (value: unknown): string => {
  const parts = getKabbalahCardPaddingParts_N(value);
  return `${parts.vertical}px ${parts.horizontal}px`;
};

export const normalizeKabbalahCardSpacing_N = (value: unknown): number => {
  const spacing = Number(value);
  if (!Number.isFinite(spacing)) return KABBALAH_CARD_SPACING_DEFAULT_N;
  return Math.min(
    Math.max(Math.round(spacing), KABBALAH_CARD_SPACING_MIN_N),
    KABBALAH_CARD_SPACING_MAX_N
  );
};

/**
 * Collapse records that describe the SAME person to the first of them.
 *
 * A person can reach the board more than once as separate rows — the public
 * form and the Add modal both create submissions, and someone who fills the
 * form twice sends two. The review queue is right to keep both (they are two
 * submissions, and each was approved on its own), but the board is not: a wall
 * showing one name twice reads as a fault, and with two or three columns it
 * fills the row with a single person while others wait for their turn.
 *
 * Identity is every field a viewer can see them by. Two rows agreeing on all of
 * it are the same person by any reading a screen can make; anything less is left
 * alone, so two genuinely different people are never merged.
 */
export const dedupeKabbalahDisplayRecords_N = <T extends Record<string, any>>(
  records: T[]
): T[] => {
  const list = Array.isArray(records) ? records.filter(Boolean) : [];
  const seen = new Set<string>();
  const unique: T[] = [];

  const part = (value: unknown): string => {
    if (value instanceof Date) return String(value.getTime());
    return String(value ?? '').trim().toLowerCase();
  };

  list.forEach((record) => {
    const identity = [
      part(record.name),
      part(record.gabbai),
      part(record.kabbalah),
      part(record.durationType),
      part(record.customDays),
    ].join('|');

    if (seen.has(identity)) return;
    seen.add(identity);
    unique.push(record);
  });

  return unique;
};

// One screenful of the live display: the row of cards the artboard shows before
// it rotates on. Splitting the records into pages is what lets a board with more
// records than columns cycle through every one of them instead of showing the
// first row and dropping the rest.
//
// A page is a plain slice of the list, so no two cards on screen ever hold the
// same record. A last page holding fewer records than the row has room for is
// left short rather than padded from the top of the list —
// `buildKabbalahDisplayCardSizes_N` spreads what it has across the row and the
// renderer centres it, so a single eligible record is one centred card instead
// of the same name repeated in every column.
export const buildKabbalahDisplayPages_N = <T>(
  records: T[],
  entriesPerCard: number,
  columns: number
): T[][] => {
  const list = Array.isArray(records) ? records.filter(Boolean) : [];
  if (!list.length) return [];

  const perCard = Math.max(1, Math.floor(entriesPerCard) || 1);
  const perPage = perCard * Math.max(1, Math.floor(columns) || 1);
  const pageCount = Math.max(1, Math.ceil(list.length / perPage));

  return Array.from({ length: pageCount }, (unusedPage, page) =>
    list.slice(page * perPage, page * perPage + perPage)
  );
};

// How the live display splits its records across cards.
//
// A card holds up to the template's entry count, so chunking by that alone
// collapses a short list into ONE card and the chosen column count is never
// visible — three columns with three records due would render as a single card.
// The row is instead spread over as many cards as the layout asks for (never
// more than one record per card), and the records are balanced across them so a
// partially filled row reads evenly, the way the editor's preview does. Order is
// preserved: card N always follows card N-1.
export const buildKabbalahDisplayCardSizes_N = (
  total: number,
  entriesPerCard: number,
  columns: number
): number[] => {
  const perCard = Math.max(1, Math.floor(entriesPerCard) || 1);
  const records = Math.max(0, Math.floor(total) || 0);
  if (!records) return [];

  const wanted = Math.min(Math.max(1, Math.floor(columns) || 1), records);
  const packedCount = Math.ceil(records / perCard);

  // Enough records to reach the chosen column count: fill each card to the
  // template's capacity, exactly as the editor preview slices them.
  if (packedCount >= wanted) {
    return Array.from({ length: packedCount }, (unused, index) =>
      Math.min(perCard, records - index * perCard)
    );
  }

  // Too few to fill even one row. Spread them over the columns instead of
  // collapsing into a single card, balanced so the row reads evenly.
  const baseSize = Math.floor(records / wanted);
  const remainder = records % wanted;

  return Array.from(
    { length: wanted },
    (unused, index) => baseSize + (index < remainder ? 1 : 0)
  );
};

const HEX_COLOR_PATTERN = /^#(?:[0-9a-f]{3}|[0-9a-f]{6}|[0-9a-f]{8})$/i;

export const isTransparentKabbalahColor_N = (value: unknown): boolean =>
  String(value ?? '').trim().toLowerCase() === KABBALAH_TRANSPARENT_COLOR_N;

// Anything that is not a hex colour or the transparent keyword falls back —
// the field is free text, so a half-typed value must never reach the canvas as
// an invalid style. The fallback is the caller's default, so a stored value the
// app can't read renders as the built-in colour rather than as nothing.
export const normalizeKabbalahColor_N = (value: unknown, fallback: string): string => {
  const color = String(value ?? '').trim();
  if (!color) return fallback;
  if (isTransparentKabbalahColor_N(color)) return KABBALAH_TRANSPARENT_COLOR_N;
  return HEX_COLOR_PATTERN.test(color) ? color : fallback;
};

// The tokens the renderer swaps for a record's own values.
//
// The name is offered three ways: whole, and as each of the two halves the
// record stores it in. A design that wants the given and family names on
// separate lines, or one of them styled differently, uses {firstName} /
// {lastName}; {fullName} is the two of them joined, which is what the shipped
// layouts print.
//
// Contact details are deliberately absent: the display endpoint never sends a
// phone number or an email address, so offering a token for them would only
// ever render as nothing.
export const KABBALAH_DESIGN_VARIABLES_N = [
  { value: 'fullName', label: 'Full Name' },
  { value: 'firstName', label: 'First Name' },
  { value: 'lastName', label: 'Last Name' },
  { value: 'gabbai', label: 'Gabbai' },
  { value: 'kabbalah', label: 'Kabbalah' },
  { value: 'duration', label: 'Kabbalos For' },
];

// Marks one record's block, and the <img> standing in for an uploaded picture.
export const KABBALAH_ENTRY_ATTR = 'data-kb-layout';
export const KABBALAH_ENTRY_VALUE = 'entry';
export const KABBALAH_PHOTO_IMAGE_ATTR = 'data-kb-photo';

// A neutral 4:3 placeholder, inline so it needs no network round trip and can
// never 404. Shown until the design has an uploaded image of its own.
export const KABBALAH_PHOTO_PLACEHOLDER =
  'data:image/svg+xml;utf8,' +
  encodeURIComponent(
    '<svg xmlns="http://www.w3.org/2000/svg" width="168" height="122">' +
      '<rect width="168" height="122" fill="#e8edf5"/>' +
      '<path d="M56 84l20-25 15 19 12-14 21 27z" fill="#c3cede"/>' +
      '<circle cx="60" cy="45" r="10" fill="#c3cede"/>' +
    '</svg>'
  );

// One record's block: the name over the length they accepted for.
const entry = (nameSize: number, detailSize: number, align = 'center') =>
  `<div ${KABBALAH_ENTRY_ATTR}="${KABBALAH_ENTRY_VALUE}">` +
    `<p style="text-align: ${align}"><span style="font-family: FbFrank; font-size: ${nameSize}px">{fullName}</span></p>` +
    `<p style="text-align: ${align}"><span style="font-family: FbFrank; font-size: ${detailSize}px">{duration}</span></p>` +
  `</div>`;

const heading = (text: string, size: number) =>
  `<p dir="rtl" style="text-align: center"><span style="font-family: TzlilimPM; font-size: ${size}px">${text}</span></p>`;

const repeat = (count: number, build: () => string) =>
  Array.from({ length: count }, build).join('');

/**
 * The board has ONE design. There is no template gallery — the Design tab opens
 * straight onto this layout's editor, and everything a gallery would have
 * offered (how many records a card draws, how many cards stand side by side,
 * the card's colours and padding) is a control on that page.
 *
 * Still an array, and still resolved through `findKabbalahTemplate_N`, so a row
 * saved against an id this build no longer ships resolves here instead of
 * breaking. The id stays `stacked`: it is what the column already defaults to
 * on the server.
 */
export const KABBALAH_DESIGN_TEMPLATES_N: KabbalahDesignTemplate_N[] = [
  {
    id: 'stacked',
    label: 'Design',
    description: 'Style the card your kabbalos are shown on, then set how many stand side by side.',
    entryCount: 3,
    defaults: {
      html: heading('מקבלי הקבלות', 26) + repeat(3, () => entry(18, 13)),
    },
  },
];

export const DEFAULT_KABBALAH_TEMPLATE_ID_N = KABBALAH_DESIGN_TEMPLATES_N[0].id;

export const findKabbalahTemplate_N = (
  templateId: string
): KabbalahDesignTemplate_N =>
  KABBALAH_DESIGN_TEMPLATES_N.find((template) => template.id === templateId) ||
  KABBALAH_DESIGN_TEMPLATES_N[0];

// A fresh copy every call — the editor mutates what it is handed, so sharing
// the constant would let one template's edits leak into another's defaults.
export const defaultKabbalahTemplateSettings_N = (
  templateId: string
): KabbalahDesignTemplateSettings_N => ({
  html: findKabbalahTemplate_N(templateId).defaults.html,
  borderColor: KABBALAH_CARD_BORDER_COLOR_DEFAULT_N,
  backgroundColor: KABBALAH_CARD_BACKGROUND_COLOR_DEFAULT_N,
  columns: 1,
  cardPadding: KABBALAH_CARD_PADDING_DEFAULT_N,
  spacing: KABBALAH_CARD_SPACING_DEFAULT_N,
});

// Whether a stored settings entry carries a design worth keeping.
const hasStoredHtml = (stored: any): boolean =>
  Boolean(stored && typeof stored === 'object' && typeof stored.html === 'string' && stored.html.trim());

/**
 * Merge a saved settings map over the built-in defaults so every template
 * always has a complete design, whatever the server had stored.
 *
 * A settings map is keyed by template id, and this build ships one template. A
 * row written when it shipped more — or under an id renamed since — would key
 * its design under something no longer in the list, and taking the built-in
 * default for the missing key would quietly wipe an organization's work. So a
 * template with nothing of its own ADOPTS the first stored design it finds
 * instead, and only falls back to the built-in when the row really is empty.
 */
export const normalizeKabbalahDesignSettings_N = (
  saved: Record<string, any> | null | undefined
): Record<string, KabbalahDesignTemplateSettings_N> => {
  const source = saved && typeof saved === 'object' && !Array.isArray(saved) ? saved : {};
  const settings: Record<string, KabbalahDesignTemplateSettings_N> = {};

  // The design a template with no entry of its own inherits. Deliberately taken
  // in the stored map's own key order, so a row carrying exactly one design has
  // no ambiguity about which one that is.
  const orphaned = Object.keys(source).find(
    (key) => !KABBALAH_DESIGN_TEMPLATES_N.some((template) => template.id === key)
      && hasStoredHtml(source[key])
  );

  KABBALAH_DESIGN_TEMPLATES_N.forEach((template) => {
    const base = defaultKabbalahTemplateSettings_N(template.id);
    const own = source[template.id];
    const stored = hasStoredHtml(own) || own ? own : (orphaned ? source[orphaned] : own);

    // Anything without usable html falls back to the template's starting design
    // rather than opening the editor empty.
    const html = hasStoredHtml(stored) ? stored.html : base.html;

    settings[template.id] = {
      html,
      borderColor: normalizeKabbalahColor_N(stored?.borderColor, base.borderColor),
      backgroundColor: normalizeKabbalahColor_N(stored?.backgroundColor, base.backgroundColor),
      columns: normalizeKabbalahCardColumns_N(stored?.columns),
      cardPadding: normalizeKabbalahCardPadding_N(stored?.cardPadding ?? base.cardPadding),
      spacing: normalizeKabbalahCardSpacing_N(stored?.spacing ?? base.spacing),
    };
  });

  return settings;
};

// The template and complete settings a stored design row resolves to. Stale or
// unknown ids fall back to the first template, and missing fields to its
// defaults, so every caller reads the same design the renderer will draw.
export const resolveKabbalahDesign_N = (
  design: { templateId?: string | null; settings?: Record<string, any> | null } | null | undefined
): { template: KabbalahDesignTemplate_N; settings: KabbalahDesignTemplateSettings_N } => {
  const template = findKabbalahTemplate_N(
    design?.templateId || DEFAULT_KABBALAH_TEMPLATE_ID_N
  );
  const settings =
    normalizeKabbalahDesignSettings_N(design?.settings)[template.id]
    || defaultKabbalahTemplateSettings_N(template.id);
  return { template, settings };
};

/**
 * How many cards stand side by side on the board.
 *
 * Read through `normalizeKabbalahDesignSettings_N` rather than off the stored
 * map directly, so this answers with the SAME design the editor and the
 * renderer resolve — including one adopted from a retired template id, whose
 * column count would otherwise be invisible here and put a three-column board
 * on a single column.
 */
export const resolveKabbalahDisplayColumns_N = (
  design: { templateId?: string | null; settings?: Record<string, any> | null } | null | undefined
): 1 | 2 | 3 => {
  const { settings } = resolveKabbalahDesign_N(design);
  return normalizeKabbalahCardColumns_N(settings.columns);
};
